import { Tooltip, Text } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { ReactNode } from "react";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { getEntity } from "@components/common/data-grid/adm-data-grid-utils";
import { AboutProductsTableEntity } from "@pages/about-products/about-products-table-types";
import { AvailabilityIcon } from "@utils/data-grid-utils/availability-cell";

interface NameWithDescriptionProps {
  /** The name to display */
  name: string;

  /** The optional description to show as a tooltip */
  description?: string;

  /** An optional identifier shown on a second line below the name */
  subtitle?: string;
}

/** Display a name with an optional description shown as a tooltip and an optional subtitle below */
export function NameWithDescription({
  name,
  description,
  subtitle,
}: NameWithDescriptionProps): React.JSX.Element {
  return (
    <Tooltip title={description ?? ""} disableHoverListener={!description}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          lineHeight: 1.2,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Text
          variant="bodyM"
          sx={{
            lineHeight: 1.2,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Text>
        {subtitle ? (
          <Text
            variant="helpText"
            sx={{
              lineHeight: 1.2,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.secondary",
            }}
          >
            {subtitle}
          </Text>
        ) : null}
      </Box>
    </Tooltip>
  );
}

/** Return the feature name cell with its description as a tooltip */
export function FeatureNameCell(
  params: EntityCellParams<AboutProductsTableEntity>
): ReactNode {
  const entity = getEntity(params);
  const tooltipDescription = entity.featureDescription || entity.featureName;

  return (
    <NameWithDescription
      name={entity.featureName}
      description={tooltipDescription}
    />
  );
}

/** Return the feature identity cell */
export function FeatureIdentityCell(
  params: EntityCellParams<AboutProductsTableEntity>
): ReactNode {
  const entity = getEntity(params);
  const tooltipDescription = entity.featureDescription || entity.featureName;

  return (
    <NameWithDescription
      name={entity.featureIdentity}
      description={tooltipDescription}
    />
  );
}

/** Return an availability cell: a check when available, a cross otherwise */
export function ProductAvailabilityCell({
  value,
}: GridRenderCellParams): ReactNode {
  return <AvailabilityIcon isAvailable={Boolean(value)} />;
}
